
import { UsageRecord } from '../../types';
import { supabase, getCurrentUserId } from '../../supabase';
import { logError } from '@/utils/errorLogger';

/**
 * Fetch usage records from Supabase
 */
export const fetchRemoteUsageRecords = async (): Promise<UsageRecord[] | null> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from('usage_records')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      logError({
        type: 'api',
        message: `Error fetching usage records: ${error.message}`,
        source: 'fetchRemoteUsageRecords',
      });
      console.error('Error fetching usage records:', error);
      return null;
    }

    // Map from database format to app format
    const records: UsageRecord[] = data.map(item => ({
      id: item.id,
      productId: item.product_id,
      quantity: item.quantity,
      date: new Date(item.date),
      groupSessionId: item.group_session_id || undefined,
      outputVolume: item.output_volume,
      consistency: item.consistency as 'liquid' | 'semi-solid' | 'solid' | 'other' | undefined,
      color: item.color,
      notes: item.notes,
    }));

    return records;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in fetchRemoteUsageRecords: ${error.message}`,
      source: 'fetchRemoteUsageRecords',
      stack: error.stack,
    });
    console.error('Error in fetchRemoteUsageRecords:', error);
    return null;
  }
};

/**
 * Add a usage record to Supabase and update product stock
 */
export const addUsageRecordToSupabase = async (
  record: UsageRecord, 
  userId: string
): Promise<boolean> => {
  try {
    // Add to Supabase
    const { error } = await supabase.from('usage_records').insert({
      id: record.id,
      user_id: userId,
      product_id: record.productId,
      quantity: record.quantity,
      output_volume: record.outputVolume,
      consistency: record.consistency,
      color: record.color,
      notes: record.notes,
      date: record.date.toISOString(),
      group_session_id: record.groupSessionId || null,
    });

    if (error) {
      logError({
        type: 'api',
        message: `Error adding usage record to Supabase: ${error.message}`,
        source: 'addUsageRecordToSupabase',
      });
      console.error('Error adding usage record to Supabase:', error);
      return false;
    }

    return true;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in addUsageRecordToSupabase: ${error.message}`,
      source: 'addUsageRecordToSupabase',
      stack: error.stack,
    });
    console.error('Error in addUsageRecordToSupabase:', error);
    return false;
  }
};

/**
 * Update the product stock in Supabase after adding a usage record
 * Uses direct database operations instead of RPC
 */
export const updateProductStockInSupabase = async (
  productId: string,
  quantity: number,
  userId: string
): Promise<boolean> => {
  try {
    // Get current product stock
    const { data: productData, error: getError } = await supabase
      .from('products')
      .select('current_stock')
      .eq('id', productId)
      .eq('user_id', userId)
      .single();
    
    if (getError) {
      logError({
        type: 'api',
        message: `Error getting product for stock update: ${getError.message}`,
        source: 'updateProductStockInSupabase',
      });
      console.error('Error getting product for stock update:', getError);
      return false;
    }
    
    // Calculate new stock (can't go below 0)
    const newStock = Math.max(0, productData.current_stock - quantity);
    
    // Update the product stock
    const { error: updateError } = await supabase
      .from('products')
      .update({ 
        current_stock: newStock, 
        updated_at: new Date().toISOString() 
      })
      .eq('id', productId)
      .eq('user_id', userId);
      
    if (updateError) {
      logError({
        type: 'api',
        message: `Error in direct product stock update: ${updateError.message}`,
        source: 'updateProductStockInSupabase',
      });
      console.error('Error in direct product stock update:', updateError);
      return false;
    }
    
    return true;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in updateProductStockInSupabase: ${error.message}`,
      source: 'updateProductStockInSupabase',
      stack: error.stack,
    });
    console.error('Error in updateProductStockInSupabase:', error);
    return false;
  }
};
